// High-score table + achievement state. Pure data, no DOM.

export interface HighScoreEntry {
  id: string;
  score: number;
  endedAtISO: string;
  wave: number;
  maxCombo: number;
  durationSec: number;
  momentumMilestones: number;
}

export interface ProgressionXp {
  level: number;
  xp: number;
}

export interface AudioSettingsPersisted {
  sound: boolean;
  haptics: boolean;
  reducedMotion: boolean;
}

export interface Persisted {
  runs: number;
  momentumMilestones: number;
  bestScore: number;
  bestCombo: number;
  bestWave: number;
  bestMomentumMilestones: number;
  highScores: HighScoreEntry[];
  achievements: string[];
  sessionsCompleted: number;
  totalRescueSeconds: number;
  progression: ProgressionXp;
  settings: AudioSettingsPersisted;
  // Career-cumulative counters (sum across all runs)
  totalBricksBroken: number;
  totalBountyBricks: number;
  totalPerfectHits: number;
  totalMissionsCompleted: number;
  totalShieldSaves: number;
  totalOverdrives: number;
  totalBossesDefeated: number;
}

export function xpForLevel(level: number): number {
  return Math.round(180 + level * 65 + Math.pow(level, 1.35) * 22);
}

/** Add xp; returns the number of levels gained (0 if none). */
export function addXp(p: Persisted, amount: number): number {
  if (amount <= 0) return 0;
  p.progression.xp += amount;
  let levels = 0;
  while (p.progression.xp >= xpForLevel(p.progression.level)) {
    p.progression.xp -= xpForLevel(p.progression.level);
    p.progression.level += 1;
    levels += 1;
  }
  return levels;
}

export const HIGH_SCORE_LIMIT = 10;

export function defaultsPersisted(): Persisted {
  return {
    runs: 0,
    momentumMilestones: 0,
    bestScore: 0,
    bestCombo: 0,
    bestWave: 1,
    bestMomentumMilestones: 0,
    highScores: [],
    achievements: [],
    sessionsCompleted: 0,
    totalRescueSeconds: 0,
    progression: { level: 1, xp: 0 },
    settings: { sound: true, haptics: true, reducedMotion: false },
    totalBricksBroken: 0,
    totalBountyBricks: 0,
    totalPerfectHits: 0,
    totalMissionsCompleted: 0,
    totalShieldSaves: 0,
    totalOverdrives: 0,
    totalBossesDefeated: 0,
  };
}

export function makeRunId(): string {
  // crypto.randomUUID exists in all evergreen targets; guard for older environments.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface RunSummary {
  score: number;
  wave: number;
  maxCombo: number;
  durationSec: number;
  momentumMilestones: number;
}

export function buildHighScoreEntry(run: RunSummary): HighScoreEntry {
  return {
    id: makeRunId(),
    score: run.score,
    endedAtISO: new Date().toISOString(),
    wave: run.wave,
    maxCombo: run.maxCombo,
    durationSec: Math.round(run.durationSec),
    momentumMilestones: run.momentumMilestones,
  };
}

/** Insert entry, sort, truncate. Returns 1-based rank if it placed, else null. */
export function insertHighScore(p: Persisted, entry: HighScoreEntry): number | null {
  const next = [...p.highScores, entry]
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return new Date(a.endedAtISO).getTime() - new Date(b.endedAtISO).getTime();
    })
    .slice(0, HIGH_SCORE_LIMIT);
  p.highScores = next;
  const idx = next.findIndex((x) => x.id === entry.id);
  return idx >= 0 ? idx + 1 : null;
}

export function formatScoreDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));
}

export function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

// ---- Achievements ----

export interface AchievementSnapshot {
  score: number;
  wave: number;
  maxCombo: number;
  durationSec: number;
  shieldSaves: number;
  perfectHits: number;
  missionsCompleted: number;
  bountyBricksBroken: number;
  bricksBroken: number;
  bossesDefeated: number;
  rank: number | null;
  overdrivesTriggered: number;
  sessionCompleted: boolean;
}

export interface AchievementDef {
  id: string;
  name: string;
  desc: string;
  check: (snap: AchievementSnapshot, p: Persisted) => boolean;
}

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  // ---- Onboarding ----
  { id: 'first_spark', name: 'First Spark', desc: 'Complete a run.', check: () => true },

  // ---- Score tiers (per run) ----
  { id: 'score_50k', name: 'Spark Collector', desc: 'Score 50,000.', check: (s) => s.score >= 50_000 },
  { id: 'score_100k', name: 'Six Figures', desc: 'Score 100,000.', check: (s) => s.score >= 100_000 },
  { id: 'score_250k', name: 'Quarter Million', desc: 'Score 250,000.', check: (s) => s.score >= 250_000 },
  { id: 'score_500k', name: 'Half a Million', desc: 'Score 500,000.', check: (s) => s.score >= 500_000 },
  { id: 'score_1m', name: 'Millionaire Storm', desc: 'Score 1,000,000.', check: (s) => s.score >= 1_000_000 },
  { id: 'score_2m', name: 'Mythic Tally', desc: 'Score 2,000,000.', check: (s) => s.score >= 2_000_000 },

  // ---- Wave tiers ----
  { id: 'wave_10', name: 'Tenfold', desc: 'Clear wave 10.', check: (s) => s.wave >= 11 },
  { id: 'wave_15', name: 'Storm Veteran', desc: 'Clear wave 15.', check: (s) => s.wave >= 16 },
  { id: 'wave_20', name: 'Endless Tide', desc: 'Clear wave 20.', check: (s) => s.wave >= 21 },
  { id: 'wave_30', name: 'Mythic Tier', desc: 'Clear wave 30.', check: (s) => s.wave >= 31 },
  { id: 'wave_50', name: 'Ascended', desc: 'Clear wave 50.', check: (s) => s.wave >= 51 },

  // ---- Duration tiers ----
  { id: 'wave_rider', name: 'Wave Rider', desc: 'Stay in for 3 minutes.', check: (s) => s.durationSec >= 180 },
  { id: 'storm_breaker', name: 'Storm Breaker', desc: 'Stay in for 5 minutes.', check: (s) => s.durationSec >= 300 },
  { id: 'marathon', name: 'Marathon', desc: 'Stay in for 10 minutes.', check: (s) => s.durationSec >= 600 },
  { id: 'iron_lungs', name: 'Iron Lungs', desc: 'Stay in for 20 minutes.', check: (s) => s.durationSec >= 1200 },

  // ---- Chain tiers ----
  { id: 'chain_apprentice', name: 'Chain Apprentice', desc: 'Reach 10 chain.', check: (s) => s.maxCombo >= 10 },
  { id: 'chain_master', name: 'Chain Master', desc: 'Reach 30 chain.', check: (s) => s.maxCombo >= 30 },
  { id: 'chain_legend', name: 'Chain Legend', desc: 'Reach 50 chain.', check: (s) => s.maxCombo >= 50 },
  { id: 'chain_mythic', name: 'Chain Mythic', desc: 'Reach 100 chain.', check: (s) => s.maxCombo >= 100 },
  { id: 'chain_ascended', name: 'Chain Ascended', desc: 'Reach 200 chain.', check: (s) => s.maxCombo >= 200 },

  // ---- Guard ----
  { id: 'clutch_guard', name: 'Clutch Guard', desc: 'Trigger a shield save.', check: (s) => s.shieldSaves >= 1 },
  { id: 'guardian', name: 'Guardian', desc: 'Trigger 5 shield saves in one run.', check: (s) => s.shieldSaves >= 5 },
  { id: 'iron_will', name: 'Iron Will', desc: 'Reach wave 10 without using a shield.', check: (s) => s.wave >= 11 && s.shieldSaves === 0 },
  { id: 'flawless', name: 'Flawless Storm', desc: 'Complete a session (4:00) without a shield save.', check: (s) => s.sessionCompleted && s.shieldSaves === 0 },

  // ---- Overdrive ----
  { id: 'no_brakes', name: 'No Brakes', desc: 'Trigger Overdrive.', check: (s) => s.overdrivesTriggered >= 1 },
  { id: 'comet_storm', name: 'Comet Storm', desc: '3 Overdrives in one run.', check: (s) => s.overdrivesTriggered >= 3 },
  { id: 'starfall', name: 'Starfall', desc: '5 Overdrives in one run.', check: (s) => s.overdrivesTriggered >= 5 },
  { id: 'eternal_storm', name: 'Eternal Storm', desc: '10 Overdrives in one run.', check: (s) => s.overdrivesTriggered >= 10 },

  // ---- Bosses ----
  { id: 'boss_slayer', name: 'Boss Slayer', desc: 'Defeat a boss brick.', check: (s) => s.bossesDefeated >= 1 },
  { id: 'crystal_hunter', name: 'Crystal Hunter', desc: 'Defeat 3 bosses in one run.', check: (s) => s.bossesDefeated >= 3 },
  { id: 'titan_slayer', name: 'Titan Slayer', desc: 'Defeat 5 bosses in one run.', check: (s) => s.bossesDefeated >= 5 },

  // ---- Skill ----
  { id: 'mission_runner', name: 'Mission Runner', desc: 'Clear 3 missions in one run.', check: (s) => s.missionsCompleted >= 3 },
  { id: 'contract_killer', name: 'Contract Killer', desc: 'Clear 10 missions in one run.', check: (s) => s.missionsCompleted >= 10 },
  { id: 'sharpshooter', name: 'Sharpshooter', desc: '25 perfect hits in one run.', check: (s) => s.perfectHits >= 25 },
  { id: 'laser_hands', name: 'Laser Hands', desc: '100 perfect hits in one run.', check: (s) => s.perfectHits >= 100 },
  { id: 'bounty_hunter', name: 'Bounty Hunter', desc: 'Break 5 bounty bricks in one run.', check: (s) => s.bountyBricksBroken >= 5 },
  { id: 'gold_rush', name: 'Gold Rush', desc: 'Break 20 bounty bricks in one run.', check: (s) => s.bountyBricksBroken >= 20 },

  // ---- Hall of Sparks ----
  { id: 'hall_entry', name: 'Hall Entry', desc: 'Place on the high-score table.', check: (s) => s.rank !== null },
  { id: 'hall_top_3', name: 'Top Three', desc: 'Place top 3 on the high-score table.', check: (s) => s.rank !== null && s.rank <= 3 },
  { id: 'new_legend', name: 'New Legend', desc: 'Take #1 high score.', check: (s) => s.rank === 1 },

  // ---- Career-cumulative (across all runs) ----
  { id: 'career_veteran', name: 'Veteran', desc: 'Finish 10 runs.', check: (_s, p) => p.runs >= 10 },
  { id: 'career_centurion', name: 'Centurion', desc: 'Finish 100 runs.', check: (_s, p) => p.runs >= 100 },
  { id: 'career_bricks_1k', name: 'Brick Bricklayer', desc: 'Break 1,000 bricks total.', check: (_s, p) => p.totalBricksBroken >= 1_000 },
  { id: 'career_bricks_10k', name: 'Storm Wrecker', desc: 'Break 10,000 bricks total.', check: (_s, p) => p.totalBricksBroken >= 10_000 },
  { id: 'career_bricks_50k', name: 'Pulverizer', desc: 'Break 50,000 bricks total.', check: (_s, p) => p.totalBricksBroken >= 50_000 },
  { id: 'career_perfect_500', name: 'Hands of Light', desc: '500 perfect hits across runs.', check: (_s, p) => p.totalPerfectHits >= 500 },
  { id: 'career_perfect_2k', name: 'Laser Identity', desc: '2,000 perfect hits across runs.', check: (_s, p) => p.totalPerfectHits >= 2_000 },
  { id: 'career_overdrive_25', name: 'Comet Junkie', desc: 'Trigger 25 Overdrives across runs.', check: (_s, p) => p.totalOverdrives >= 25 },
  { id: 'career_overdrive_100', name: 'Comet King', desc: 'Trigger 100 Overdrives across runs.', check: (_s, p) => p.totalOverdrives >= 100 },
  { id: 'career_bosses_10', name: 'Boss Hunter', desc: 'Defeat 10 bosses across runs.', check: (_s, p) => p.totalBossesDefeated >= 10 },
  { id: 'career_bosses_50', name: 'Titanslayer', desc: 'Defeat 50 bosses across runs.', check: (_s, p) => p.totalBossesDefeated >= 50 },
  { id: 'career_bounty_100', name: 'Bounty Career', desc: 'Break 100 bounty bricks across runs.', check: (_s, p) => p.totalBountyBricks >= 100 },
  { id: 'career_missions_50', name: 'Contract Master', desc: 'Clear 50 missions across runs.', check: (_s, p) => p.totalMissionsCompleted >= 50 },
  { id: 'career_sessions_5', name: 'Survivor', desc: 'Complete 5 rescue sessions (4:00 each).', check: (_s, p) => p.sessionsCompleted >= 5 },
  { id: 'career_sessions_25', name: 'Wave Master', desc: 'Complete 25 rescue sessions.', check: (_s, p) => p.sessionsCompleted >= 25 },
  { id: 'career_hour', name: 'Hour of Calm', desc: 'Bank 1 hour of rescue time.', check: (_s, p) => p.totalRescueSeconds >= 3600 },
  { id: 'career_day', name: 'Day of Calm', desc: 'Bank 24 hours of rescue time.', check: (_s, p) => p.totalRescueSeconds >= 86_400 },
];

export function evaluateAchievements(
  p: Persisted,
  snap: AchievementSnapshot,
): AchievementDef[] {
  const unlocked: AchievementDef[] = [];
  const have = new Set(p.achievements);
  for (const a of ACHIEVEMENTS) {
    if (have.has(a.id)) continue;
    if (a.check(snap, p)) {
      unlocked.push(a);
      p.achievements.push(a.id);
    }
  }
  return unlocked;
}
